import React from 'react';
import { resolveTip, roundMoney } from 'shared-models/browser';
import { ValueEntryModal } from '../../../../../components/ValueEntryModal/ValueEntryModal';
import type { ValueEntryResult } from '../../../../../components/ValueEntryModal/ValueEntryModal';
import type { TipInputMode, TipPayload } from '../../../../../services/payments/payments.service';

type TipModalProps = {
  isOpen: boolean;
  title: string;
  /** percent basis: cart/items total for the order tip, the line amount for a line tip */
  base: number;
  /** order tip → offer the scope cards (על כל ההזמנה / חד פעמי); line tip → no scope */
  showScope: boolean;
  currentMode: TipInputMode | null;
  currentValue: number | null;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (payload: TipPayload) => void;
};

const TIP_PCT_PRESETS = [10, 12, 15, 20];
const TIP_AMT_PRESETS = [5, 10, 20, 50];
const TIP_MAX_PERCENT = 200;
const TIP_MAX_VALUE = 9999;

const TIP_SCOPE = {
  allLabel: 'טיפ על כל ההזמנה',
  allSub: 'מתעדכן עם פריטים שיתווספו',
  snapshotLabel: 'טיפ חד פעמי',
  snapshotSub: 'סכום קבוע על המצב הנוכחי',
};

// Tip entry on the shared ValueEntryModal (discount-modal design). Scope decides persistence:
// percent + 'all' → live percent; percent + 'חד פעמי' / amount → frozen ₪ value. See FUNCTIONS.md.
const TipModalComponent: React.FC<TipModalProps> = ({
  isOpen,
  title,
  base,
  showScope,
  currentMode,
  currentValue,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const existingAmount = resolveTip(currentMode ?? null, currentValue ?? null, base);

  const toPayload = (result: ValueEntryResult): TipPayload => {
    if (result.amount <= 0) {
      return { tipInputMode: null, tipInputValue: null };
    }
    if (result.mode === 'percentage' && result.scope === 'all') {
      return { tipInputMode: 'percent', tipInputValue: result.rawInput };
    }
    // amount mode, or a one-time ('חד פעמי') percent → freeze to a fixed ₪ value
    return { tipInputMode: 'value', tipInputValue: roundMoney(result.amount) };
  };

  return (
    <ValueEntryModal
      isOpen={isOpen}
      title={title}
      initialMode="amount"
      percentBase={base}
      infoRightLabel="סה&quot;כ פריטים"
      infoRightValue={base}
      inputLabels={{ percentage: 'אחוז טיפ', amount: 'סכום טיפ' }}
      presets={{ percentage: TIP_PCT_PRESETS, amount: TIP_AMT_PRESETS }}
      scope={showScope ? TIP_SCOPE : undefined}
      maxPercent={TIP_MAX_PERCENT}
      maxAmount={TIP_MAX_VALUE}
      capPercentMessage="אחוז הטיפ חייב להיות בין 0 ל-200"
      capAmountMessage="הטיפ גבוה מהמותר"
      existingAmount={existingAmount}
      dataTestId="tip"
      removeLabel="בטל טיפ"
      isSaving={isSubmitting}
      onClose={onClose}
      onConfirm={(result) => onConfirm(toPayload(result))}
      onRemove={() => onConfirm({ tipInputMode: null, tipInputValue: null })}
    />
  );
};

export const TipModal = React.memo(TipModalComponent);
